import {useCallback} from 'react'

import {NO_GROUP_VALUE} from '../../../api/memex-items/contracts'
import {compareDescending} from '../../../helpers/iterations'
import {useEnabledFeatures} from '../../../hooks/use-enabled-features'
import type {ColumnModel} from '../../../models/column-model'
import {isIterationColumnModel} from '../../../models/column-model/guards'
import {
  getVerticalGroupsForField,
  isVerticalGroupMetadata,
  MissingVerticalGroupId,
  type VerticalGroup,
} from '../../../models/vertical-group'
import {usePaginatedMemexItemsQuery} from '../../../state-providers/memex-items/queries/use-paginated-memex-items-query'
import {useFilteredGroups} from './use-filtered-groups'
import {useVerticalGroupedBy} from './use-vertical-grouped-by'

/**
 * Calls the useFilteredGroups hook with the current vertical grouping field, as well as
 * a function for determining the list of groups for a given field.
 * @returns An array of groups, or undefined if no grouping is currently active.
 */
export const useVerticalGroups = () => {
  const {groupedByColumnId} = useVerticalGroupedBy()

  const {memex_mwl_server_group_order, memex_table_without_limits} = useEnabledFeatures()
  const useServerGroups = memex_table_without_limits && memex_mwl_server_group_order
  // Feature flags will not change after initial render
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const paginatedMemexItems = useServerGroups ? usePaginatedMemexItemsQuery() : null

  const getServerVerticalGroups = useCallback(
    (columnModel?: ColumnModel, opts: {includeAllCompletedIterations?: boolean} = {}): Array<VerticalGroup> => {
      if (!columnModel) return []
      const serverGroups = paginatedMemexItems?.queriesForGroups?.flatMap(q => q.data?.groups ?? []) ?? []
      const groups = serverGroups.map(group => {
        const isNoValueGroup = group.groupValue === NO_GROUP_VALUE
        const name = isNoValueGroup ? `No ${columnModel.name}` : group.groupValue
        const groupMetadata = isVerticalGroupMetadata(group.groupMetadata) ? group.groupMetadata : undefined
        return {
          // TODO: Remove dependence on MissingVerticalGroupId
          // https://github.com/github/projects-platform/issues/2127
          id: isNoValueGroup ? MissingVerticalGroupId : group.groupId,
          name,
          nameHtml: name, // TODO - move nameHtml to groupMetadata
          groupMetadata,
        }
      })
      // TODO - Remove this once Iterations are truncated server-side
      // https://github.com/github/projects-platform/issues/2145
      if (isIterationColumnModel(columnModel) && opts.includeAllCompletedIterations === false) {
        // Sort completed iterations by descending start date (most recent first)
        const sortedCompletedIterations = columnModel.settings.configuration.completedIterations.sort(compareDescending)
        // Collect id of any completed iterations beyond the most recent 3
        const excludedIterationIds = sortedCompletedIterations.slice(3).map(iteration => iteration.id)
        // Return all groups except those with excluded Iteration (metadata) ids
        return groups.filter(group => !excludedIterationIds.includes(group.groupMetadata?.id ?? ''))
      }
      return groups
    },
    [paginatedMemexItems?.queriesForGroups],
  )

  const getVerticalGroups = useServerGroups ? getServerVerticalGroups : getVerticalGroupsForField
  // TODO - skip filtering server groups once filtering is applied server-side
  // https://github.com/github/projects-platform/issues/2141
  return {...useFilteredGroups(groupedByColumnId, getVerticalGroups), groupedByColumnId}
}
